import React, {
  useState, useMemo, useCallback, useEffect, useRef,
} from 'react'

import { useSelector, useDispatch } from 'react-redux'
import { useApolloClient } from '@apollo/client'
import _isEmpty from 'lodash/isEmpty'
import { selectIsBreakpoint, selectApp } from '../../services/app/selectors'
import { parse as parseQuery } from '../../services/query-string/index'
import { selectAuth } from '../../services/auth/selectors'
import { selectPrimaryLanguage } from '../../services/user/selectors'
import { selectDefaultOraclePersonalityId } from '../../services/feature-tracking/selectors'
import { HISTORY_METHOD_REPLACE } from '../../services/navigation/index'
import { setAiGa4Event } from '../../services/event-tracking/actions'
import { setAppHeader } from '../../services/app/actions'
import {
  AI_ERROR_RESPONSE_GA4_EVENT,
  AI_MESSAGE_RECEIVED_GA4_EVENT,
  AI_MESSAGE_FAILURE_GA4_EVENT,
  AI_UNRECOGNIZED_INPUT_GA4_EVENT,
  AI_CONVERSATION_RESUMED_GA4_EVENT,
  AI_PAGE_VIEW_CHAT_HOME_GA4_EVENT,
  AI_RECOMMENDATION_RECEIVED_GA4_EVENT,
  AI_RATE_LIMITED_GA4_EVENT,
} from '../../services/event-tracking/index'
import { EN } from '../../services/languages/constants'
import { setOraclePersona } from '../../services/oracle/actions'
import { hasOracleAccessEntitlement } from '../../services/subscription/index'
import {
  usePostHogEvent,
  EventContextProvider,
  ChatSessionContextProvider,
  DivinationChannel,
} from '../PostHog/index'
import { URL_AI_CHAT } from '../../services/url/constants'
import { useTranslatedText } from '../OracleCommons/StaticTextContext'
import { OracleSidebar } from './OracleSidebar'
import {
  buildDivinationDestination,
  parseDivinationEntry,
} from './sidebarNavigationQuery'
import { Location, History } from './history-types'
import {
  useOraclePersonalityListQuery,
  useGetChatSessionQuery,
  useRateLimitStatusQuery,
  useDivinationCollectionsByTypeQuery,
  GetHistoryDocument,
} from './queries.generated'
import { useOracleSessionManager } from './oracleSessionManager'
import { useOraclePersonality, useOracleChatSubscription } from './hooks'
import { useIsEnglishUser } from './hooks/useIsEnglishUser'
import { OracleExistingChat } from './OracleExistingChat'
import { OracleMobileDrawerButton } from './OracleMobileDrawerButton'
import { OracleErrorToast } from '../OracleCommons/OracleErrorToast'
import { useDivinationTracking } from './hooks/useDivinationTracking'
import { OracleNewChat } from './OracleNewChat'
import { TarotEntryScreen } from './TarotEntryScreen'
import { isGqlAborted } from './isGqlAborted'
import OracleVoiceChat from './OracleVoiceChat'
import { OracleLoadingSpinner } from '../OracleCommons'
import {
  shouldApplyRateLimit,
  getUserTimezone,
  scheduleNextMidnightReset,
  RateLimitState,
  buildRateLimitState,
} from './rateLimitUtils'
import { oracleHistoryRedirect } from './utils/oracleHistoryRedirect'
import { FEATURE_TO_CHANNEL, getInvitationAction, getInvitationPersonalityId } from './oracleChatSessionConfig'
import { useTarotSeedQuestions } from './hooks/useTarotSeedQuestions'
import { useTarotFeatureState } from './hooks/useTarotFeatureState'
import { categorizeTarotSeedsFromParsed, buildTarotNewReadingMessage, buildTarotPullMessage } from './hooks/seedQuestionTagUtils'
import { PERSONALITY_SAGE_ID, getPersonalityKeyById } from './personalityFilter'
import { getChatExperienceName } from './chatExperienceName'
import { randomHex } from './utils/randomHex'
import { useTarotReframeModal } from './hooks/useTarotReframeModal'
import { TarotReframeModal } from './TarotReframeModal'
import { TarotTheaterProvider } from './TarotTheater/TarotTheaterProvider'
import { useTarotTheaterContext } from './TarotTheater/TarotTheaterContext'

// Inner component that lives INSIDE TarotTheaterProvider and wires the reframe
// modal's onAccept / onClose to the theater FSM (v2 path only). Keeping this as
// a separate named component rather than an inline function ensures that
// OracleResponsiveLayout (which MOUNTS the provider) cannot call
// useTarotTheaterContext() at its own top level — that would violate the rule
// that a component cannot consume its own provider.
interface ReframeModalWithTheaterProps {
  reframeModal: ReturnType<typeof useTarotReframeModal>
  sendChat: (message: string, channelOverride?: string, personalityIdOverride?: string) => void
  location: { search: string; pathname: string }
  history: History
  // ImmutableMap shape as required by oracleHistoryRedirect
  auth: import('immutable').Map<string, unknown>
  language: string | undefined
}

const ReframeModalWithTheater: React.FC<ReframeModalWithTheaterProps> = ({
  reframeModal,
  sendChat,
  location,
  history,
  auth,
  language,
}) => {
  const theater = useTarotTheaterContext()

  return (
    <TarotReframeModal
      isOpen={reframeModal.isOpen}
      originalQuestion={reframeModal.originalQuestion}
      suggestions={reframeModal.suggestions}
      activeSuggestionIndex={reframeModal.activeSuggestionIndex}
      onClose={() => {
        reframeModal.close()
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { chatSessionId: _ignored, ...cleanQuery } = parseQuery(location.search)
        oracleHistoryRedirect({
          history,
          url: location.pathname,
          query: cleanQuery,
          auth,
          language,
          historyMethod: HISTORY_METHOD_REPLACE,
        })
        if (theater) {
          theater.cancel()
        }
      }}
      onRefresh={reframeModal.refresh}
      onAccept={(question, wasEdited) => {
        const opts = wasEdited ? undefined : { forceAccept: true }
        sendChat(buildTarotPullMessage(question, opts), 'tarot', String(PERSONALITY_SAGE_ID))
        reframeModal.close()
        if (theater) {
          theater.submitQuestion()
        }
      }}
    />
  )
}

interface OracleResponsiveLayoutProps {
  location: Location
  history: History
  mobileSidebarOpen?: boolean
  onMobileSidebarToggle?: () => void
}

export const OracleResponsiveLayout: React.FC<OracleResponsiveLayoutProps> = ({
  location,
  history,
  mobileSidebarOpen = false,
  onMobileSidebarToggle,
}) => {
  const isBelowSidebarBreakpoint = useSelector(selectIsBreakpoint(1024))
  const [appendText, setAppendText] = useState('')
  const [showErrorToast, setShowErrorToast] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | undefined>()
  const [streamCompletionCount, setStreamCompletionCount] = useState(0)
  const [resumedConversation, setResumedConversation] = useState(true)
  const resumedConversationRef = useRef(resumedConversation)

  // Keep ref in sync with state
  useEffect(() => {
    resumedConversationRef.current = resumedConversation
  }, [resumedConversation])

  const auth = useSelector(selectAuth)
  const jwtRef = useRef(auth.get('jwt'))
  jwtRef.current = auth.get('jwt')
  const language = useSelector(selectPrimaryLanguage)
  const dispatch = useDispatch()
  // Error message translations
  const sessionLoadFailedError = useTranslatedText({ keys: ['errors', 'sessionLoadFailed'] })
  const personalityLoadFailedError = useTranslatedText({ keys: ['errors', 'personalityLoadFailed'] })
  const subscriptionFailedError = useTranslatedText({ keys: ['errors', 'subscriptionFailed'] })

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const isWebView = useSelector((state: any) => selectApp(state).get('isWebView', false))

  // Get current query parameters
  const currentQuery = parseQuery(location.search)

  const currentDivinationEntry = parseDivinationEntry(location.pathname, URL_AI_CHAT)
  const showTarotEntry = currentDivinationEntry === 'tarot'
  const [tarotEntryInitialQuestion, setTarotEntryInitialQuestion] = useState('')
  const lastOpenedBadQuestionRef = useRef<string | null>(null)
  const reframeModal = useTarotReframeModal()
  const { openFromGql: openReframeModalFromGql } = reframeModal

  // Get default personality from feature tracking
  const defaultOraclePersonalityId = useSelector(selectDefaultOraclePersonalityId)

  // Check if user's primary language is English (for hiding Architect+ from non-English users)
  const isEnglishUser = useIsEnglishUser()

  // Get current chat session ID from URL
  const currentChatSessionId = currentQuery.chatSessionId as string

  // Get chat session data if we have an existing session
  const { data: chatSessionData, error: chatSessionError } = useGetChatSessionQuery({
    variables: { chatSessionId: currentChatSessionId },
    skip: !currentChatSessionId,
    context: {
      jwt: auth.get('jwt'),
    },
  })

  // Get personality data for components that need it
  const { data: personalityData, error: personalityError } = useOraclePersonalityListQuery({
    variables: {
      limit: 5,
      offset: 0,
      order: [['id', 'ASC']],
    },
    context: {
      jwt: auth.get('jwt'),
    },
  })

  // Handle query errors with toast
  React.useEffect(() => {
    if (chatSessionError && !chatSessionData) {
      setErrorMessage(sessionLoadFailedError)
      setShowErrorToast(true)
    }
  }, [chatSessionError, chatSessionData, sessionLoadFailedError])

  React.useEffect(() => {
    if (personalityError && !personalityData) {
      setErrorMessage(personalityLoadFailedError)
      setShowErrorToast(true)
    }
  }, [personalityError, personalityData, personalityLoadFailedError])

  // Use personality hook for selection logic
  const {
    activePersonalityId,
    selectedPersonality,
    showRegLink,
  } = useOraclePersonality({
    location,
    currentChatSessionId,
    chatSessionData,
    personalityData,
    defaultOraclePersonalityId,
    isEnglishUser,
  })

  const contentRef = useRef<HTMLDivElement>(null)
  const [pageViewEventSent, setPageViewEventSent] = useState(false)

  // Rate limiting — users with oracle subscription codes bypass; others get trial (if flag on)
  const hasOracleAccess = hasOracleAccessEntitlement(auth.get('subscriptions'))
  const isRateLimitApplicable = shouldApplyRateLimit({ hasOracleAccess })
  const userTimezone = useMemo(() => getUserTimezone(), [])

  const {
    data: textRateLimitData,
    refetch: refetchTextRateLimit,
  } = useRateLimitStatusQuery({
    skip: !isRateLimitApplicable,
    variables: {
      timezone: userTimezone,
      mode: 'text',
    },
    context: {
      jwt: auth.get('jwt'),
    },
  })

  const {
    data: voiceRateLimitData,
    refetch: refetchVoiceRateLimit,
  } = useRateLimitStatusQuery({
    skip: !isRateLimitApplicable,
    variables: {
      timezone: userTimezone,
      mode: 'voice',
    },
    context: {
      jwt: auth.get('jwt'),
    },
  })

  // Persistent voice mode state - independent of URL parameter
  const [isInVoiceMode, setIsInVoiceMode] = useState(false)

  useEffect(() => {
    const selectedPersonalityId = selectedPersonality?.id
    if (!selectedPersonalityId) return

    // Update Personality
    dispatch(setOraclePersona(selectedPersonalityId))

    // Send AI GA4 event
    if (!pageViewEventSent) {
      dispatch(setAiGa4Event(AI_PAGE_VIEW_CHAT_HOME_GA4_EVENT))
      trackPageViewChatHome()
      setPageViewEventSent(true)
    }
  }, [selectedPersonality, dispatch, pageViewEventSent])

  // Use shared session manager
  const sessionManager = useOracleSessionManager({ location, history })
  const {
    currentSessionId,
    isNewSession,
    handleSessionReceived,
  } = sessionManager

  const apolloClient = useApolloClient()

  // PostHog events
  const trackModerationViolation = usePostHogEvent('ai_moderation_violation')
  const trackMessageReceived = usePostHogEvent('ai_message_received')
  const trackPageViewChatHome = usePostHogEvent('ai_page_view_chat_home')
  const trackUnrecognizedInput = usePostHogEvent('ai_unrecognized_input')
  const trackRateLimited = usePostHogEvent('ai_rate_limited')
  // Divination analytics tracking (tarot, oracle cards)
  const preChatChannel = chatSessionData?.oracleChatSession?.chatChannel || null
  const divinationChannel = (preChatChannel === 'tarot' || preChatChannel === 'oracle_cards')
    ? preChatChannel as DivinationChannel
    : undefined
  const { handleDivinationDocument, handleChannelJoin } = useDivinationTracking(divinationChannel)

  // Use chat subscription hook for streaming responses
  const chatSubscription = useOracleChatSubscription({
    personalityId: activePersonalityId,
    currentSessionId,
    isNewSession,
    mode: isInVoiceMode ? 'voice' : 'text',
    skip: isInVoiceMode, // Skip text subscription when in voice mode
    jwt: auth.get('jwt'),
    isWebView,
    userTimezone,
    isRateLimitApplicable,
    language: language || EN,
    chatChannel: chatSessionData?.oracleChatSession?.chatChannel || null,
    onSessionReceived: handleSessionReceived,
    onComplete: () => {
      setStreamCompletionCount((c) => c + 1)
      // Evict cached DivinationFeatureState so ConversationPillButtons
      // picks up the updated canPullCard value when it mounts after
      // the card animation finishes.
      apolloClient.cache.evict({ fieldName: 'divinationFeatureState' })
      apolloClient.cache.gc()
      // Refetch tarot seed questions and feature state after stream completion
      if (isTarotCards) {
        refetchTarotSeeds()
        evictAndRefetchTarotState()
      }
      // Refetch sidebar history directly — use jwtRef to avoid unstable closure
      const jwt = jwtRef.current
      apolloClient.query({
        query: GetHistoryDocument,
        variables: {
          limit: 100, offset: 0, favorite: true, order: [['activityUpdatedAt', 'DESC']],
        },
        context: { jwt, useMaster: true },
        fetchPolicy: 'network-only',
      })
      apolloClient.query({
        query: GetHistoryDocument,
        variables: {
          limit: 100, offset: 0, favorite: false, order: [['activityUpdatedAt', 'DESC']],
        },
        context: { jwt, useMaster: true },
        fetchPolicy: 'network-only',
      })
    },
    onError: (error) => {
      const isAborted = isGqlAborted(error)
      if (!isAborted) {
        setShowErrorToast(true)
        setErrorMessage(subscriptionFailedError)
      }

      // Only track backend errors in GA4, not network/client-side errors
      // Backend errors come from errorInfo and are formatted as "ERROR_CODE: message"
      // This pattern matches all backend error codes
      const isBackendError = error?.message && /^[A-Z_]+:\s/.test(error.message)

      if (isBackendError && !isAborted) {
        // AI GA4 event - only for backend errors
        dispatch(setAiGa4Event(AI_ERROR_RESPONSE_GA4_EVENT))
      }
    },
    onMessageGa4Events: (data) => {
      const {
        success, error, messagePairId, responseLength,
      } = data
      const hasError = !_isEmpty(error)
      const hasValidationError = hasError ? error.toLowerCase().includes('input') : false
      const messageReceived = success && !hasError

      // Handle message success or failure
      const event = messageReceived ? AI_MESSAGE_RECEIVED_GA4_EVENT : AI_MESSAGE_FAILURE_GA4_EVENT
      dispatch(setAiGa4Event(event))

      // PostHog tracking
      if (messageReceived) {
        trackMessageReceived(messagePairId, responseLength, 'Text')
      }

      if (hasValidationError) {
        // Handle input validation error
        dispatch(setAiGa4Event(AI_UNRECOGNIZED_INPUT_GA4_EVENT))
        trackUnrecognizedInput('input validation failed', 'Text', 'Text Field')
      }

      // Handle conversation resumed
      if (messageReceived && resumedConversationRef.current) {
        dispatch(setAiGa4Event(AI_CONVERSATION_RESUMED_GA4_EVENT))
        setResumedConversation(false)
      }
    },
    onRecommendationReceived: () => {
      // Track when AI recommends videos (documents.size > 0)
      dispatch(setAiGa4Event(AI_RECOMMENDATION_RECEIVED_GA4_EVENT))
    },
    onModerationError: (categories, severity) => {
      // Track content moderation violations in PostHog
      trackModerationViolation(severity, categories)
    },
    onDivinationDocument: handleDivinationDocument,
  })

  // Destructure hook return values
  const {
    streamingText: chatStreamingText,
    isStreaming,
    pendingMessage,
    sessionTitle: streamChatSessionTitle,
    subscriptionRateLimitState: hookRateLimitState,
    documents: streamingDocuments,
    chatChannel: subscriptionChatChannel,
    sendMessage: sendChat,
    stopStreaming: handleStop,
    moderationError,
    resetRateLimitState,
  } = chatSubscription

  // Prefer subscription channel (live), fall back to session query (page load).
  // Gate the session-query channel on whether the fetched data actually
  // corresponds to the URL's chatSessionId. Without this, Apollo can briefly
  // return a previous session's data while the new query is in flight — e.g.
  // navigating /aichat/conversation/tarot → ?chatSessionId=B caused B to
  // render with the previous chat's tarot channel, triggering the "Draw your
  // card" UI on a non-tarot session (AI-821).
  const sessionDataMatchesUrl = !!currentChatSessionId
    && chatSessionData?.oracleChatSession?.id === currentChatSessionId
  const fetchedChatChannel = sessionDataMatchesUrl
    ? chatSessionData?.oracleChatSession?.chatChannel
    : null
  const renderChatChannel = subscriptionChatChannel || fetchedChatChannel || null

  // AI-768 in-chat Layer 3 producer values. ChatSessionContextProvider below
  // makes these visible to every event captured under this subtree.
  // - aiPersona: persona key derived from useOraclePersonality's
  //   already-prioritized activePersonalityId (session → URL → settings default).
  // - chatExperienceName: Title Case `chat_experience_name` from the live
  //   chat channel; null pre-session so events fired on chat home don't
  //   misreport an experience the user hasn't entered yet.
  // - conversationTitle: prefer the streaming-subscription value while a
  //   new chat is forming; fall back to the persisted GraphQL session name
  //   (gated on sessionDataMatchesUrl for the same anti-stale reason as
  //   fetchedChatChannel above).
  const aiPersona = getPersonalityKeyById(activePersonalityId) ?? null
  const chatExperienceName = currentChatSessionId
    ? getChatExperienceName(renderChatChannel)
    : null
  const fetchedConversationTitle = sessionDataMatchesUrl
    ? chatSessionData?.oracleChatSession?.name ?? null
    : null
  const conversationTitle = streamChatSessionTitle || fetchedConversationTitle

  // Fetch divination card deck data for greeting/shuffle screen
  const isOracleCards = renderChatChannel === 'oracle_cards'
  const isTarotCards = renderChatChannel === 'tarot'
  const isDivinationChannel = isOracleCards || isTarotCards
  const divinationCollectionType = isTarotCards ? 'tarot' : 'oracle_cards'
  const { data: divinationData } = useDivinationCollectionsByTypeQuery({
    variables: { type: divinationCollectionType },
    context: { jwt: auth.get('jwt') },
    skip: !isDivinationChannel,
  })
  const cardBackImageUrl = divinationData?.divinationCollectionsByType?.[0]?.backImageUrl
    || undefined

  // Tarot seed questions — lifted here so categorized data can be passed to both
  // ConversationPillButtons (clarify + others) and the FAB / mobile header (start)
  const {
    seedQuestions: tarotRawSeeds,
    refetch: refetchTarotSeeds,
  } = useTarotSeedQuestions({ skip: !isTarotCards })

  const tarotCategorizedSeeds = useMemo(
    () => categorizeTarotSeedsFromParsed(tarotRawSeeds),
    [tarotRawSeeds],
  )

  const {
    isCurrentMonth: isTarotCurrentMonth,
    isInBadQuestionState: tarotIsInBadQuestionState,
    badQuestionOriginal: tarotBadQuestionOriginal,
    badQuestionRephrasedQuestions: tarotBadQuestionSuggestions,
    loading: tarotFeatureStateLoading,
    evictAndRefetch: evictAndRefetchTarotState,
  } = useTarotFeatureState({
    chatSessionId: currentChatSessionId,
    skip: !isTarotCards,
  })

  // Bad-question recovery: divinationFeatureState is the single source of truth.
  // Open the modal whenever the server reports a fresh bad-question verdict. We
  // dedupe on the full verdict CONTENT (the original question PLUS the rephrase
  // suggestions), not the original alone. This way a FRESH reframe verdict (new
  // suggestions) reopens the modal even when the backend keeps the same
  // badQuestionOriginal across two consecutive bad questions, while an identical
  // re-render (same original AND same suggestions) still does NOT reopen
  // (preventing an open loop). This subsumes the earlier AI-811 behavior, which
  // reopened only when badQuestionOriginal changed. Reset the tracker when state
  // flips back to ok so a future bad question reopens the modal even if the
  // verdict content matches a previously dismissed one.
  useEffect(() => {
    if (tarotFeatureStateLoading) return
    if (!isTarotCards) return
    if (!tarotIsInBadQuestionState) {
      lastOpenedBadQuestionRef.current = null
      return
    }
    if (tarotBadQuestionSuggestions.length === 0) return
    if (!tarotBadQuestionOriginal) return
    // Join on U+241F (SYMBOL FOR UNIT SEPARATOR) — a sentinel that won't appear
    // in user questions or LLM rephrase suggestions, so the parts can't collide.
    const verdictKey = [tarotBadQuestionOriginal, ...tarotBadQuestionSuggestions].join('␟')
    if (lastOpenedBadQuestionRef.current === verdictKey) return
    lastOpenedBadQuestionRef.current = verdictKey
    openReframeModalFromGql({
      originalQuestion: tarotBadQuestionOriginal,
      suggestions: tarotBadQuestionSuggestions,
      chatSessionId: currentChatSessionId,
    })
  }, [
    tarotFeatureStateLoading,
    isTarotCards,
    tarotIsInBadQuestionState,
    tarotBadQuestionOriginal,
    tarotBadQuestionSuggestions,
    currentChatSessionId,
    openReframeModalFromGql,
  ])

  // Set up midnight reset scheduler for rate limiting (daily limits only, not lifetime)
  useEffect(() => {
    if (!isRateLimitApplicable || !hasOracleAccess) return undefined

    const cleanup = scheduleNextMidnightReset(() => {
      // Reset subscription state at midnight
      resetRateLimitState()
      // Refetch queries as backup/verification
      refetchTextRateLimit()
      refetchVoiceRateLimit()
    })

    // Cleanup on unmount
    return cleanup
  }, [
    isRateLimitApplicable, hasOracleAccess, resetRateLimitState,
    refetchTextRateLimit, refetchVoiceRateLimit,
  ])

  // Create rate limit state object for components
  // Priority: subscription state → query state → defaults
  const rateLimitState: RateLimitState = buildRateLimitState({
    isRateLimitApplicable,
    subscriptionState: hookRateLimitState,
    textQueryData: textRateLimitData,
    voiceQueryData: voiceRateLimitData,
    refetchText: refetchTextRateLimit,
    refetchVoice: refetchVoiceRateLimit,
  })

  // Track rate limit event firing using localStorage with daily key
  // This ensures the event only fires once per day, even across page refreshes
  // Requirement: "once a day ONLY" when user is notified they're rate limited
  useEffect(() => {
    if (!isRateLimitApplicable) return

    // Get today's date string in user's timezone for the localStorage key
    const getTodayKey = (): string => {
      const now = new Date()
      const timezone = getUserTimezone()
      try {
        // Format: YYYY-MM-DD in user's timezone
        const dateStr = now.toLocaleDateString('en-CA', { timeZone: timezone })
        return `ai_rate_limited_event_${dateStr}`
      } catch {
        // Fallback to UTC if timezone formatting fails
        const dateStr = now.toISOString().split('T')[0]
        return `ai_rate_limited_event_${dateStr}`
      }
    }

    const todayKey = getTodayKey()
    const eventFiredToday = localStorage.getItem(todayKey) === 'true'

    // Check if user is rate limited (either text or voice)
    const isTextRateLimited = rateLimitState.rateLimited
    const isVoiceRateLimited = rateLimitState.voiceRateLimitState?.rateLimited ?? false
    const isAnyRateLimited = isTextRateLimited || isVoiceRateLimited

    // Only fire event if:
    // 1. User is currently rate limited
    // 2. Event hasn't been fired today (persists across page refreshes)
    if (isAnyRateLimited && !eventFiredToday) {
      // Determine UI location based on which mode is rate limited
      const uiLocation = isVoiceRateLimited && isInVoiceMode
        ? 'AI Voice Mode'
        : 'AI Chat'

      dispatch(setAiGa4Event(AI_RATE_LIMITED_GA4_EVENT.mergeDeep({
        ui_location: uiLocation,
      })))
      trackRateLimited()

      // Mark event as fired for today (persists across page refreshes)
      localStorage.setItem(todayKey, 'true')
    }
  }, [
    isRateLimitApplicable,
    rateLimitState.rateLimited,
    rateLimitState.voiceRateLimitState?.rateLimited,
    isInVoiceMode,
    dispatch,
  ])

  // Set resumed conversation when session changes
  const prevSessionIdRef = useRef(currentSessionId)
  useEffect(() => {
    const previousSessionId = prevSessionIdRef.current
    setResumedConversation(previousSessionId !== null)
    prevSessionIdRef.current = currentSessionId
  }, [currentSessionId])

  // Reset appendText when chat session ID changes (URL navigation)
  useEffect(() => {
    // Don't reset if we're currently streaming (new chat in progress)
    if (isStreaming) {
      return
    }
    setAppendText('')
  }, [currentSessionId, isStreaming])

  const promptFromQuery = useMemo(() => {
    const query = parseQuery(location.search)
    return query.prompt as string | undefined
  }, [location.search])

  const featureFromQuery = useMemo(() => {
    const query = parseQuery(location.search)
    return query.feature as string | undefined
  }, [location.search])

  // Initialize voice mode state and clean up URL on fresh page load, keep it in
  // a variable outside of the URL to avoid race conditions.
  const hasPerformedInitialCleanup = useRef(false)
  useEffect(() => {
    if (!hasPerformedInitialCleanup.current) {
      hasPerformedInitialCleanup.current = true

      const query = parseQuery(location.search)
      if (query.voiceMode === 'true') {
        setIsInVoiceMode(true)
        // Remove voiceMode from URL while preserving other parameters
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { voiceMode, ...queryWithoutVoiceMode } = query

        oracleHistoryRedirect({
          history,
          url: location.pathname,
          query: queryWithoutVoiceMode,
          auth,
          language,
          historyMethod: HISTORY_METHOD_REPLACE,
        })
      }
    }
  }, [location.search, history, location.pathname, auth, language])

  const openTarotEntry = useCallback((initialQuestion?: string, replace?: boolean) => {
    setTarotEntryInitialQuestion(initialQuestion ?? '')
    const { url, query } = buildDivinationDestination(
      parseQuery(location.search) as Record<string, unknown>,
      URL_AI_CHAT,
      'tarot',
    )
    oracleHistoryRedirect({
      history,
      url,
      query,
      auth,
      language,
      ...(replace ? { historyMethod: HISTORY_METHOD_REPLACE } : {}),
    })
  }, [location.search, history, auth, language])

  const closeTarotEntry = useCallback(() => {
    history.goBack()
  }, [history])

  const enterVoiceMode = useCallback(() => {
    dispatch(setAppHeader(false)) // Hide header for full-screen voice mode
    setIsInVoiceMode(true)
    const query = parseQuery(location.search)
    oracleHistoryRedirect({
      history,
      url: location.pathname,
      query: { ...query, voiceMode: 'true' },
      auth,
      language,
      historyMethod: HISTORY_METHOD_REPLACE,
    })
  }, [dispatch, location.search, history, location.pathname, auth, language])

  const exitVoiceMode = useCallback(() => {
    dispatch(setAppHeader(true)) // Show header when exiting voice mode
    setIsInVoiceMode(false)
    const query = parseQuery(location.search)
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { voiceMode, ...queryWithoutVoiceMode } = query
    oracleHistoryRedirect({
      history,
      url: location.pathname,
      query: queryWithoutVoiceMode,
      auth,
      language,
      historyMethod: HISTORY_METHOD_REPLACE,
    })
  }, [dispatch, location.search, history, location.pathname, auth, language])

  // Manage app header state based on voice mode
  useEffect(() => {
    dispatch(setAppHeader(!isInVoiceMode && !isWebView))
  }, [dispatch, isInVoiceMode, isWebView])

  const handleInvitationClick = useCallback((feature: string) => {
    const channel = FEATURE_TO_CHANNEL[feature]
    if (!channel) return

    const action = getInvitationAction(feature)
    const personalityOverride = getInvitationPersonalityId(
      feature,
      activePersonalityId.toString(),
    )

    // Auto-send synthetic message to initiate the agent-first interaction.
    // Channel is passed directly to the subscription input, not via URL.
    sendChat(action, channel, personalityOverride)
    handleChannelJoin()
  }, [sendChat, activePersonalityId, handleChannelJoin])

  const onNewChat = () => {
    const query = parseQuery(location.search)
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { chatSessionId, ...everythingElse } = query

    // Reset streaming state to ensure clean state for new chat
    handleStop()

    // New chat always lands on chat home, not a divination sub-path (AI-821)
    oracleHistoryRedirect({
      history,
      url: URL_AI_CHAT,
      query: everythingElse,
      auth,
      language,
      historyMethod: HISTORY_METHOD_REPLACE,
    })
    // Also close the mobile sidebar
    onMobileSidebarToggle()
  }

  const isOperationInProgress = isStreaming

  // Track the previous URL session ID to detect conversation switches
  const prevUrlSessionIdRef = useRef<string | undefined>()

  // Clear stream state when switching to a different conversation
  useEffect(() => {
    const urlSessionId = parseQuery(location.search).chatSessionId as string
    const prevUrlSessionId = prevUrlSessionIdRef.current

    // Update the ref for next render
    prevUrlSessionIdRef.current = urlSessionId

    // CRITICAL FIX: Clear stream state when switching between different existing conversations
    // Only trigger when we have both a previous session ID and a new different session ID
    if (urlSessionId && prevUrlSessionId && urlSessionId !== prevUrlSessionId && !isStreaming) {
      // Clear stream state when switching to a different existing conversation
      // This prevents cross-chat contamination
      handleStop()
    }
  }, [location.search, isStreaming, handleStop])

  const renderContent = () => {
    // When a chat subscription is in flight and we don't yet have a session,
    // collapse the content area to a spinner. This avoids briefly flashing the
    // welcome/invitation/chat-input UI between submitting a question (from the
    // tarot entry screen, the welcome chat box, or a `?prompt=` deep-link) and
    // the session being created.
    if (isStreaming && !currentSessionId) {
      return <OracleLoadingSpinner />
    }
    if (showTarotEntry) {
      return (
        <TarotEntryScreen
          initialQuestion={tarotEntryInitialQuestion}
          onBack={() => {
            setTarotEntryInitialQuestion('')
            closeTarotEntry()
          }}
          onSubmit={(message) => {
            sendChat(message, 'tarot', String(PERSONALITY_SAGE_ID))
            handleChannelJoin()
            // Replace (not goBack) so the prior session ID does not flash here (AI-822)
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const { chatSessionId, ...rest } = parseQuery(location.search)
            oracleHistoryRedirect({
              history,
              url: URL_AI_CHAT,
              query: rest,
              auth,
              language,
              historyMethod: HISTORY_METHOD_REPLACE,
            })
          }}
        />
      )
    }
    if (currentSessionId) {
      // Parse temporal scroll params from URL
      const scrollParams = (() => {
        const q = parseQuery(location.search)
        const scrollTo = q.scrollTo as string | undefined
        const pos = q.pos as string | undefined
        const snippet = q.snippet as string | undefined
        const matchSource = q.matchSource as string | undefined
        if (scrollTo && pos && /^\d+$/.test(pos)) {
          return {
            scrollTargetPairId: scrollTo,
            scrollTargetIndex: parseInt(pos, 10),
            snippet: snippet ? decodeURIComponent(snippet) : undefined,
            matchSource,
          }
        }
        return null
      })()

      // Callback to clear temporal scroll params after successful scroll
      // Keep searchActive flag so highlights persist until user exits search
      const clearScrollParams = () => {
        const q = parseQuery(location.search)
        const {
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          scrollTo,
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          pos,
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          snippet,
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          matchSource,
          ...rest
        } = q

        // Keep searchActive if it exists - it gets cleared when user exits search
        const newQuery = { ...rest }
        if (q.searchActive) {
          newQuery.searchActive = q.searchActive as string
        }
        oracleHistoryRedirect({
          history,
          url: location.pathname,
          query: newQuery,
          auth,
          language,
          historyMethod: HISTORY_METHOD_REPLACE,
        })
      }

      // Check if search is active
      const isSearchActive = (parseQuery(location.search).searchActive as string) === 'true'

      return (
        <OracleExistingChat
          sendChat={sendChat}
          currentChatSessionId={currentSessionId}
          isOperationInProgress={isOperationInProgress}
          onStop={handleStop}
          pendingMessage={pendingMessage}
          streamingText={chatStreamingText}
          streamChatSessionTitle={streamChatSessionTitle}
          streamingDocuments={streamingDocuments}
          onEnterVoiceMode={enterVoiceMode}
          personalityKey={selectedPersonality?.key}
          rateLimitState={rateLimitState}
          scrollTargetPairId={scrollParams?.scrollTargetPairId}
          scrollTargetIndex={scrollParams?.scrollTargetIndex}
          highlightSnippet={scrollParams?.snippet}
          highlightMatchSource={scrollParams?.matchSource}
          isSearchActive={isSearchActive}
          onScrolledToTarget={clearScrollParams}
          moderationError={moderationError}
          chatChannel={renderChatChannel}
          cardBackImageUrl={cardBackImageUrl}
          streamCompletionCount={streamCompletionCount}
          isStreaming={isStreaming}
          tarotCategorizedSeeds={isTarotCards ? tarotCategorizedSeeds : undefined}
          isTarotCurrentMonth={isTarotCurrentMonth}
          isReframeModalOpen={reframeModal.isOpen}
        />
      )
    }
    return (
      <>
      <OracleNewChat
          sendChat={sendChat}
          appendText={appendText}
          isOperationInProgress={isOperationInProgress}
          onStop={handleStop}
          pendingMessage={pendingMessage}
          streamingText={chatStreamingText}
          onEnterVoiceMode={enterVoiceMode}
          rateLimitState={rateLimitState}
          showRegLink={showRegLink}
          moderationError={moderationError}
          onTarotStart={openTarotEntry}
          activePersonalityId={activePersonalityId}/>
      </>
    )
  }

  useEffect(() => {
    if (
      promptFromQuery
      && !currentSessionId
      && !isStreaming
      && activePersonalityId
    ) {
      // Use the subscription to send the prompt message
      sendChat(promptFromQuery)
    }
  }, [
    promptFromQuery,
    currentSessionId,
    isStreaming,
    activePersonalityId,
    sendChat,
  ])

  // Auto-start divination session when navigating from member home with ?feature=tarot etc.
  useEffect(() => {
    if (
      featureFromQuery
      && !currentSessionId
      && !isStreaming
      && activePersonalityId
    ) {
      handleInvitationClick(featureFromQuery)
    }
  }, [
    featureFromQuery,
    currentSessionId,
    isStreaming,
    activePersonalityId,
    handleInvitationClick,
  ])

  const handleConversationSelect = useCallback(() => {
    // Clear any active streaming state when switching conversations
    if (isStreaming) {
      handleStop()
    }

    // Close mobile sidebar if open
    if (isBelowSidebarBreakpoint && mobileSidebarOpen) {
      onMobileSidebarToggle()
    }
  }, [isStreaming, handleStop, isBelowSidebarBreakpoint, mobileSidebarOpen, onMobileSidebarToggle])

  // Reset the mobile sidebar's scroll position once it has finished sliding
  // closed. Without this, reopening the drawer shows it scrolled to wherever
  // the user left it. We use transitionend (rather than a timer) so the reset
  // happens off-screen and is invisible to the user.
  const mobileSidebarRef = useRef<HTMLDivElement>(null)
  const handleMobileSidebarTransitionEnd = useCallback(
    (e: React.TransitionEvent<HTMLDivElement>) => {
      if (e.target !== e.currentTarget) return
      if (e.propertyName !== 'transform') return
      if (mobileSidebarOpen) return
      const scrollable = mobileSidebarRef.current?.querySelector('.oracle-sidebar')
      if (scrollable instanceof HTMLElement) {
        scrollable.scrollTop = 0
      }
    },
    [mobileSidebarOpen],
  )

  const eventContext = divinationChannel || 'ai_sage_on_stage'

  // Voice mode gets full-screen layout, bypassing normal responsive layout
  if (isInVoiceMode) {
    return (
      <EventContextProvider context={eventContext}>
        <ChatSessionContextProvider
          chatSessionId={currentChatSessionId || null}
          conversationTitle={conversationTitle}
          aiPersona={aiPersona}
          chatExperienceName={chatExperienceName}
        >
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          zIndex: 9999,
          background: 'var(--oracle-bg-primary, #ffffff)',
        }}>
          <OracleVoiceChat
            onExit={exitVoiceMode}
            currentSessionId={currentSessionId}
            personalityId={activePersonalityId}
            location={location}
            onSessionReceived={handleSessionReceived}
            voiceRateLimitState={rateLimitState?.voiceRateLimitState}
          />
          <OracleErrorToast
            isVisible={showErrorToast}
            onClose={() => {
              setShowErrorToast(false)
              setErrorMessage(undefined)
            }}
            message={errorMessage}
          />
        </div>
        </ChatSessionContextProvider>
      </EventContextProvider>
    )
  }

  return (
    <EventContextProvider context={eventContext}>
      <ChatSessionContextProvider
        chatSessionId={currentChatSessionId || null}
        conversationTitle={conversationTitle}
        aiPersona={aiPersona}
        chatExperienceName={chatExperienceName}
      >
      <TarotTheaterProvider
        active={renderChatChannel === 'tarot'}
        streamingDocuments={streamingDocuments}
        isStreaming={isStreaming}
        isInBadQuestionState={tarotIsInBadQuestionState}
      >
      <div className="oracle-responsive-layout">
        <div className="oracle-responsive-layout__sidebar">
          {!isBelowSidebarBreakpoint && (
            <OracleSidebar
              location={location}
              history={history}
              onItemSelect={handleConversationSelect}
              onNewChat={onNewChat}
              currentPersonalityId={activePersonalityId?.toString()}
            />
          )}
        </div>

        <div className="oracle-responsive-layout__main">
          {isBelowSidebarBreakpoint && onMobileSidebarToggle && !showTarotEntry && (
            <OracleMobileDrawerButton
              onClick={onMobileSidebarToggle}
              className="oracle-responsive-layout__mobile-drawer-button"
              location={location}
              history={history}
              isInConversation={!!currentChatSessionId}
              isEnglishUser={isEnglishUser}
              tarotStartSeed={isTarotCards ? tarotCategorizedSeeds.start : undefined}
              onTarotNewReading={isTarotCards && tarotCategorizedSeeds.start
                ? () => {
                  const salt = randomHex(8)
                  sendChat(buildTarotNewReadingMessage(tarotCategorizedSeeds.start!.question, salt))
                }
                : undefined}
            />
          )}
          <div className="oracle-responsive-layout__content" ref={contentRef}>
            {renderContent()}
          </div>
        </div>

        {/* Mobile Sidebar Overlay */}
        {isBelowSidebarBreakpoint && (
          <>
            <div
              ref={mobileSidebarRef}
              className={`oracle-responsive-layout__mobile-sidebar ${mobileSidebarOpen ? 'oracle-responsive-layout__mobile-sidebar--open' : ''}`}
              onTransitionEnd={handleMobileSidebarTransitionEnd}
            >
              <OracleSidebar
                location={location}
                history={history}
                onItemSelect={handleConversationSelect}
                onNewChat={onNewChat}
                onClose={onMobileSidebarToggle}
                currentPersonalityId={activePersonalityId?.toString()}
              />
            </div>
            <div
              className={`oracle-responsive-layout__mobile-backdrop ${mobileSidebarOpen ? 'oracle-responsive-layout__mobile-backdrop--open' : ''}`}
              onClick={onMobileSidebarToggle}
              aria-hidden="true"
            />
          </>
        )}

        <OracleErrorToast
          isVisible={showErrorToast}
          onClose={() => {
            setShowErrorToast(false)
            setErrorMessage(undefined)
          }}
          message={errorMessage}
        />
        <ReframeModalWithTheater
          reframeModal={reframeModal}
          sendChat={sendChat}
          location={location}
          history={history}
          auth={auth}
          language={language}
        />
      </div>
      </TarotTheaterProvider>
      </ChatSessionContextProvider>
    </EventContextProvider>
  )
}
